/**
 * 메일 작성 모달 (v2.108.0) — 포털 문서를 본인 네이버웍스 계정으로 발송.
 *
 * App 에 한 번 마운트되는 전역 모달 (lib/mail-compose.ts 외부 store).
 * 어느 화면이든 openMailCompose({ docRefs, subject, ... }) 로 연다.
 *
 * 첨부 3원천:
 *   ① 문서(docRefs) — 서버가 그 자리에서 만들거나 DB 보관분에서 꺼낸다 (내용은 안 올림)
 *   ② 로컬 파일 — 내 PC 에서 고른 파일 (multipart)
 *   ③ 포털 보관 문서 — 영업건을 골라 그 건의 첨부에서 선택 (→ ① 의 attachment 좌표로)
 *
 * ⚠ 배경 클릭 닫기 금지 (check:modal 규칙) — 닫기는 ×·취소 버튼만.
 */

import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  docRefWire,
  downloadLocalFile,
  downloadMailAttachment,
  getMailAccount,
  getMailAttachmentNames,
  getMailSignature,
  mailSignatureImageUrl,
  searchRecipients,
  sendMail,
  type MailDocRef,
  type MailSignatureView,
  type RecipientSuggestion,
} from '../lib/mail-api';
import { isCompanyBlockTable } from '../lib/mail-signature-template';
import {
  closeMailCompose,
  getMailComposeState,
  subscribeMailCompose,
} from '../lib/mail-compose';
import { MAIL_PICKER_HIDDEN_KINDS, listAttachments, type AttachmentMeta } from '../lib/attachment-api';
import { SalesPickerModal } from './SalesPickerModal';
import { NasFilePicker } from './NasFilePicker';
import { nasFileKey } from '../lib/nas-api';
import { useAuth } from '../lib/auth-context';
import { RichEditor } from './RichEditor';
import {
  EMPTY_DOC,
  MAIL_DOC_LIMITS,
  docForEdit,
  docForSave,
  docIsEmpty,
  docToPlainText,
  imageIdsOf,
  textToRichDoc,
  type RichDoc,
  type RichNode,
} from '../lib/rich-doc';
import { useDialogLayer } from '../lib/dialog-stack';
import { LoadingLines } from './ui/Skeleton';

// 서버 lib/mail-send/validate.ts 의 EMAIL_RE 와 같은 성질(구분자·공백 배제)을 유지할 것
// v2.228.1 — 점이 겹치거나 앞뒤에 붙은 주소도 거절(서버와 같은 글자)
const EMAIL_RE = /^(?!.*\.\.)[^\s@;,<>"'.](?:[^\s@;,<>"']*[^\s@;,<>"'.])?@[^\s@;,<>"'.]+(?:\.[^\s@;,<>"'.]+)+$/;
const MAX_TOTAL = 25 * 1024 * 1024;
/** 첨부 개수 상한 — 서버 lib/mail-send/validate.ts 의 MAX_ATTACH_COUNT 와 같은 값 */
const MAX_ATTACH_COUNT = 20;

const DOC_TYPE_LABEL: Record<string, string> = {
  quote: '견적서 PDF',
  'quote-xlsx': '견적서 엑셀',
  po: '발주서 PDF',
  'tech-support': '기술지원확약서 PDF',
  'tech-support-docx': '기술지원확약서 워드',
  'trade-statement': '거래명세서',
  'delivery-confirmation': '납품확인증',
  nameplate: '검수명판',
  delivery: '납품계 세트',
  commencement: '착수계 세트',
  'bell-status': '비상벨 납기현황',
  attachment: '보관 문서',
  'nas-file': 'NAS 파일',
};

function fmtSize(n: number): string {
  if (n >= 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)}MB`;
  if (n >= 1024) return `${Math.round(n / 1024)}KB`;
  return `${n}B`;
}

const SOURCE_BADGE: Record<RecipientSuggestion['source'], { label: string; cls: string }> = {
  works: { label: '주소록', cls: 'bg-emerald-100 text-emerald-700' },
  /** 포털의 거래처 담당자 주소록 (v2.195.0 · 종전에는 메일 쓸 때 검색되지 않았다) */
  contact: { label: '담당자', cls: 'bg-violet-100 text-violet-700' },
  partner: { label: '거래처', cls: 'bg-blue-100 text-blue-700' },
  employee: { label: '직원', cls: 'bg-slate-200 text-slate-600' },
};

/* ── 수신자 칩 입력 (자동완성) ─────────────────────────────── */
function RecipientInput({
  label,
  chips,
  onChange,
  defaultAddr,
  defaultHint,
  onRemoveDefault,
}: {
  label: string;
  chips: string[];
  onChange: (next: string[]) => void;
  /**
   * 기본으로 들어가는 주소 (v2.176.0 영업 공용 메일 · 「뺄 수 없음」 → v2.250.0 「× 로 뺄 수 있음」).
   * ⭐서버가 발송 직전에 붙이는 주소라 **칩 목록(chips)에 넣지 않는다** — × 는 onRemoveDefault 로 부모에 알리고,
   *   부모가 salesCc=false 를 서버에 보낸다. 칩만 지우고 서버에 안 알리면 「화면엔 없는데 실제로는 가는」 최악이 된다.
   */
  defaultAddr?: string | null;
  defaultHint?: string;
  onRemoveDefault?: () => void;
}) {
  const [input, setInput] = useState('');
  const [sugs, setSugs] = useState<RecipientSuggestion[]>([]);
  const [showSugs, setShowSugs] = useState(false);
  const [invalidMsg, setInvalidMsg] = useState<string | null>(null);
  const timer = useRef<number | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  // 늦게 도착한 검색 응답이 (blur/선택으로 비운) 드롭다운을 도로 여는 것 방지 — 최신 질의만 반영
  const querySeq = useRef(0);
  useEffect(() => {
    if (timer.current) window.clearTimeout(timer.current);
    const q = input.trim();
    const mySeq = ++querySeq.current;
    if (!q) {
      setSugs([]);
      return;
    }
    timer.current = window.setTimeout(() => {
      searchRecipients(q)
        .then((r) => {
          if (querySeq.current !== mySeq) return; // 이미 다른 입력 상태 — 무시
          setSugs(r.suggestions.filter((s) => !chips.includes(s.email)));
          setShowSugs(true);
        })
        .catch(() => setSugs([]));
    }, 250);
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  const commit = (raw: string): void => {
    const v = raw.trim().replace(/[;,]$/, '');
    if (!v) return;
    if (!EMAIL_RE.test(v)) {
      setInvalidMsg(`이메일 형식이 아닙니다: ${v}`);
      return;
    }
    setInvalidMsg(null);
    if (!chips.includes(v)) onChange([...chips, v]);
    setInput('');
    setSugs([]);
  };

  return (
    <div ref={boxRef} className="relative">
      <div className="flex items-start gap-2">
        <div className="w-16 pt-2 text-xs font-medium text-slate-500 shrink-0">{label}</div>
        <div className="flex-1 min-h-[38px] px-2 py-1.5 border border-slate-300 rounded flex flex-wrap gap-1 items-center focus-within:border-blue-400">
          {defaultAddr && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-slate-100 text-slate-600 border border-slate-300 rounded text-xs"
              title={defaultHint ?? '기본으로 참조됩니다'}
            >
              {defaultAddr}
              {onRemoveDefault && (
                <button
                  type="button"
                  className="text-slate-400 hover:text-slate-700"
                  onClick={onRemoveDefault}
                  title="이 메일에서는 빼기"
                  aria-label={`${defaultAddr} 빼기`}
                >
                  ×
                </button>
              )}
            </span>
          )}
          {/* 기본 주소를 사람이 또 적었으면 한 번만 보여 준다 */}
          {chips.filter((c) => !defaultAddr || c.trim().toLowerCase() !== defaultAddr.toLowerCase()).map((c) => (
            <span
              key={c}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 text-blue-800 rounded text-xs"
            >
              {c}
              <button
                type="button"
                className="text-blue-400 hover:text-blue-700"
                onClick={() => onChange(chips.filter((x) => x !== c))}
                title="제거"
              >
                ×
              </button>
            </span>
          ))}
          <input
            type="text"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              setInvalidMsg(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ';' || e.key === ',') {
                e.preventDefault();
                commit(input);
              } else if (e.key === 'Backspace' && !input && chips.length) {
                onChange(chips.slice(0, -1));
              } else if (e.key === 'Escape') {
                setShowSugs(false);
              }
            }}
            onBlur={() => {
              // 입력 중이던 주소는 포커스가 떠날 때 칩으로 굳힌다 (Enter 누락 대비)
              window.setTimeout(() => setShowSugs(false), 150);
              if (input.trim()) commit(input);
            }}
            placeholder={chips.length ? '' : '이메일 입력 또는 검색 (이름·거래처)'}
            className="flex-1 min-w-[160px] text-sm outline-none py-0.5"
          />
        </div>
      </div>
      {invalidMsg && (
        <div className="ml-[72px] mt-0.5 text-xs text-rose-600">{invalidMsg}</div>
      )}
      {defaultAddr && defaultHint && (
        <div className="ml-[72px] mt-0.5 text-xs text-slate-500">{defaultHint}</div>
      )}
      {showSugs && sugs.length > 0 && (
        <div className="absolute left-[72px] right-0 top-full mt-0.5 z-10 bg-white border border-slate-200 rounded shadow-lg max-h-52 overflow-y-auto">
          {sugs.slice(0, 12).map((s) => (
            <button
              key={`${s.source}:${s.email}`}
              type="button"
              className="w-full px-2.5 py-1.5 text-left hover:bg-blue-50 flex items-center gap-2 text-sm"
              // onClick 은 input blur 뒤라 안 닿는다 — mousedown 시점에 처리
              onMouseDown={(e) => {
                e.preventDefault();
                if (!chips.includes(s.email)) onChange([...chips, s.email]);
                setInput('');
                setSugs([]);
              }}
            >
              <span className={`px-1 py-0.5 rounded text-[11px] shrink-0 ${SOURCE_BADGE[s.source].cls}`}>
                {SOURCE_BADGE[s.source].label}
              </span>
              <span className="font-medium text-slate-700 shrink-0">{s.name}</span>
              {/* 소속 (v2.195.0) — 「검색한 목록 나올 때 소속회사도 같이 보이게」.
                  ⚠긴 회사 이름이 메일 주소를 밀어내지 않게 **둘 다** 줄여서 보여 준다(각자 제 몫만 차지). */}
              {s.company ? (
                <span className="text-slate-500 text-xs truncate max-w-[40%]">{s.company}</span>
              ) : null}
              <span className="text-slate-500 text-xs truncate">{s.email}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── 포털 보관 문서 선택 (영업건 → 첨부 목록) ─────────────────── */
function PortalDocPicker({
  open,
  initialSalesId,
  existingIds,
  onClose,
  onAdd,
}: {
  open: boolean;
  initialSalesId: string | null;
  existingIds: Set<string | number>;
  onClose: () => void;
  onAdd: (items: AttachmentMeta[]) => void;
}) {
  const [salesId, setSalesId] = useState<string | null>(initialSalesId);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [rows, setRows] = useState<AttachmentMeta[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [checked, setChecked] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!open) return;
    setSalesId(initialSalesId);
    setChecked(new Set());
    setErr(null);
    setRows([]);
    if (!initialSalesId) setPickerOpen(true);
  }, [open, initialSalesId]);

  useEffect(() => {
    if (!open || !salesId) return;
    setLoading(true);
    setErr(null);
    // 영업건을 바꾸면 체크도 함께 리셋 — 첨부 id 는 전역 PK 라 다른 건의 목록엔 없는 id 가
    // checked 에 남아 「N개 첨부」 개수·활성이 어긋난다
    setChecked(new Set());
    // v2.233.1·v2.233.2 — 판매보고서·고객 발주서는 메일로 붙일 일이 없어 목록에서 뺀다(사용자 지시 · attachment-api 한 곳에서 정함)
    listAttachments('sales', salesId)
      .then((r) => setRows(r.filter((a) => !MAIL_PICKER_HIDDEN_KINDS.includes(a.kind))))
      .catch((e) => setErr((e as Error).message))
      .finally(() => setLoading(false));
  }, [open, salesId]);

  if (!open) return null;

  return (
    // 작성 모달(z-90) 위에 떠야 한다. 안의 SalesPickerModal(z-70)은 이 overlay 가 만드는
    // 스태킹 컨텍스트 안에서 DOM 순서로 위에 그려진다 (e2e 실측).
    <div className="fixed inset-0 z-[95] bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="ui-subsection-title">📁 포털 보관 문서에서 첨부</h3>
          <button
            type="button"
            onClick={onClose}
            className="ui-close"
          >
            ×
          </button>
        </div>
        <div className="p-4 flex-1 overflow-y-auto">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs text-slate-500">영업건</span>
            <span className="font-mono text-sm">{salesId ?? '(미선택)'}</span>
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              className="btn btn-secondary btn-sm"
            >
              영업건 선택…
            </button>
          </div>
          {err && (
            <div className="mb-2 px-3 py-2 bg-rose-50 border border-rose-200 text-rose-800 rounded text-sm">
              {err}
            </div>
          )}
          {loading ? (
            <div className="text-center text-slate-400 py-8 text-sm"><LoadingLines /></div>
          ) : rows.length === 0 && salesId ? (
            <div className="text-center text-slate-400 py-8 text-sm">이 영업건에 메일로 붙일 보관 문서가 없습니다</div>
          ) : (
            <table className="w-full text-[13px]">
              <tbody>
                {rows.map((a) => {
                  const already = existingIds.has(a.id);
                  return (
                    <tr key={a.id} className="border-t border-slate-100">
                      <td className="py-1.5 pr-2 w-6">
                        <input
                          type="checkbox"
                          disabled={already}
                          checked={already || checked.has(a.id)}
                          onChange={(e) => {
                            const next = new Set(checked);
                            if (e.target.checked) next.add(a.id);
                            else next.delete(a.id);
                            setChecked(next);
                          }}
                        />
                      </td>
                      <td className="py-1.5 pr-2">
                        <span className="inline-block px-1.5 py-0.5 mr-1.5 bg-slate-100 text-slate-600 rounded text-xs">
                          {a.kind}
                        </span>
                        <span className={already ? 'text-slate-400' : 'text-slate-800'}>
                          {a.filename}
                        </span>
                      </td>
                      <td className="py-1.5 text-right text-xs text-slate-500 whitespace-nowrap">
                        {fmtSize(a.file_size)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
          >
            취소
          </button>
          <button
            type="button"
            disabled={checked.size === 0}
            onClick={() => {
              onAdd(rows.filter((r) => checked.has(r.id)));
              onClose();
            }}
            className="btn btn-primary"
          >
            {checked.size ? `${checked.size}개 첨부` : '첨부'}
          </button>
        </div>
      </div>
      <SalesPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(row) => setSalesId(row.salesId)}
        title="첨부를 가져올 영업건 선택"
      />
    </div>
  );
}

/* ── 메일 꼬리말 (v2.125.0) ─────────────────────────────
 * 마이페이지 [메일 꼬리말] 에 등록해 둔 인사말·연락처를 본문 맨 아래에 붙인다.
 * ⭐서버가 발송 순간에 몰래 붙이지 않고 **화면이 미리 넣는 이유**: 보내기 전에 눈으로 보고
 *   그 메일에서만 고치거나 지울 수 있어야 한다. 「나간 메일에 내가 못 본 글이 붙어 있다」는
 *   대외 발송에서 가장 나쁜 모양이다.
 */

/** 끝에 붙은 빈 줄을 걷는다 — 인사말과 회사 정보 표 사이에 빈 줄이 두 겹 끼지 않게 */
function trimTrailingBlank(nodes: RichNode[]): RichNode[] {
  let end = nodes.length;
  while (end > 0) {
    const n = nodes[end - 1]!;
    if (n.type !== 'paragraph' || (n.content?.length ?? 0) > 0) break;
    end -= 1;
  }
  return nodes.slice(0, end);
}

/**
 * 꼬리말 → 편집기에 넣을 문서. 꺼져 있거나 넣을 것이 없으면 null.
 * ⭐두 부분이다(v2.211.0): ① 인사말(내가 마이페이지에 저장한 것) + 빈 줄 + ② 회사 정보 표(공통 ·
 *   조회할 때 내 직원 정보로 지어 둔 것). 인사말이 비어 있으면 표만 붙는다.
 */
function signatureDocOf(sig: MailSignatureView | undefined, force = false): RichDoc | null {
  if (!sig) return null;
  if (!sig.enabled && !force) return null;
  const personal = docForEdit(sig.doc, mailSignatureImageUrl);
  const head = docIsEmpty(personal) ? [] : trimTrailingBlank(personal.content);
  const company = sig.companyBlock ?? [];
  const d: RichDoc = {
    type: 'doc',
    content: [...head, ...(head.length && company.length ? [{ type: 'paragraph' } as RichNode] : []), ...company],
  };
  return docIsEmpty(d) ? null : d;
}

/**
 * 본문 끝에 꼬리말을 붙인다 — 사이에 **빈 줄 하나**.
 * ⭐본문이 비어 있을 때 꼬리말이 맨 위에 붙어 버리면 글을 쓸 자리가 없다
 *   (2026-09-03 「빈 칸이 다음 입력 자리를 없앤다」와 같은 함정).
 */
function withSignature(body: RichDoc, sig: RichDoc): RichDoc {
  return { type: 'doc', content: [...body.content, { type: 'paragraph' }, ...sig.content] };
}

/**
 * 지금 본문에 꼬리말이 들어 있나 — ⭐⭐⭐**깃발이 아니라 본문을 보고 판정한다.**
 * 「넣었다」는 상태값으로 판정하면 두 가지가 동시에 어긋난다:
 *   · 사람이 꼬리말을 지워도 안내가 계속 「들어갔습니다」라고 **거짓말**을 한다
 *   · [+ 꼬리말 넣기] 가 이미 들어 있는데도 **한 벌 더** 붙인다
 * (2026-09-03 「값이 저장돼 있나」로 상태를 판정하지 말 것 — 「실제로 갈리나」로 볼 것)
 */
function bodyHasSignature(body: RichDoc, sig: RichDoc): boolean {
  const miss = missingSignatureParts(body, sig);
  return miss.personal.length === 0 && miss.company.length === 0;
}

/**
 * 꼬리말 중 본문에 **없는 부분** — [+ 꼬리말 넣기] 는 이것만 넣는다(있는 부분을 한 벌 더 붙이지 않게).
 * ⭐회사 정보 표는 **표 자체**로 본다(`isCompanyBlockTable` — 남색 띠). `docToPlainText` 는 표를 일부러
 *   버리므로(회의록·메일 body_text 규칙) 글자로는 표가 있는지 알 수 없다 (v2.211.0).
 * 인사말은 종전대로 글자로, 글자 없이 그림만 있으면 그림 번호로 본다.
 */
function missingSignatureParts(body: RichDoc, sig: RichDoc): { personal: RichNode[]; company: RichNode[] } {
  const company = sig.content.filter(isCompanyBlockTable);
  const personal = trimTrailingBlank(sig.content.filter((n) => !isCompanyBlockTable(n)));
  const companyIn = company.length === 0 || body.content.some(isCompanyBlockTable);
  let personalIn = true;
  const personalDoc: RichDoc = { type: 'doc', content: personal };
  const personalText = docToPlainText(personalDoc).trim();
  if (personalText) {
    personalIn = docToPlainText(body).includes(personalText);
  } else {
    const ids = imageIdsOf(personalDoc);
    if (ids.length) {
      const inBody = new Set(imageIdsOf(body));
      personalIn = ids.every((id) => inBody.has(id));
    }
  }
  return { personal: personalIn ? [] : personal, company: companyIn ? [] : company };
}

/* ── 본체 ─────────────────────────────────────────────── */
function ComposeBody({ onClose }: { onClose: () => void }) {
  const { init } = getMailComposeState();
  const qc = useQueryClient();

  /**
   * ⭐창을 열 때 **캐시에서 동기로** 꺼내 처음부터 붙은 상태로 뜬다 — 늦게 도착해 툭 튀어나오면
   *   사람이 이미 쓰기 시작한 글 아래로 끼어든다. (바깥 껍데기가 미리 받아 둔다)
   */
  const initialSigDoc = signatureDocOf(qc.getQueryData<MailSignatureView>(['mail', 'signature']));

  // 받는사람을 미리 채워 줄 수 있다 (v2.140.0 · 딜등록 만료 안내가 제안사 주소를 넘긴다). 창에서 고칠 수 있다.
  const [to, setTo] = useState<string[]>(init.to ?? []);
  const [cc, setCc] = useState<string[]>(init.cc ?? []);
  // 영업 공용 메일(sales@)을 참조에 붙이나 — 기본 true, 참조칸의 그 칩 × 로 뺀다 (v2.250.0 · 사용자 「삭제도 할 수 있게」).
  //   ⭐서버가 붙이는 주소라 화면에서 칩만 지우면 그래도 간다 → 뺐다는 사실을 payload `salesCc` 로 서버에 알린다(sendMail).
  const [salesCc, setSalesCc] = useState(true);
  const [subject, setSubject] = useState(init.subject ?? '');
  /**
   * 본문 — 서식 있는 문서 (v2.124.0). 미리 채운 글(init.body)은 문단으로 바꿔 넣는다.
   * 발송 때 `docForSave` 로 새 그림을 dataUrl 로 실어 보내고, 서버가 HTML 을 짓는다.
   */
  const [bodyDoc, setBodyDoc] = useState<RichDoc>(() => {
    // 서식 있는 본문(표 등)을 받았으면 그것이 본문이다 — 없을 때만 글자를 문단으로 바꾼다 (v2.190.0)
    const base = init.bodyDoc ?? (init.body ? textToRichDoc(init.body) : EMPTY_DOC);
    return initialSigDoc ? withSignature(base, initialSigDoc) : base;
  });
  const [docRefs, setDocRefs] = useState<MailDocRef[]>(init.docRefs ?? []);
  const [files, setFiles] = useState<File[]>(init.files ?? []);
  /**
   * 답장·전달 (v2.205.0) — 전달이면 원문 첨부를 **처음엔 전부** 골라 둔다(보통 첨부째 넘긴다).
   * 보낼 때 번호만 서버로 가고, 파일은 서버가 웍스에서 다시 받아 싣는다.
   */
  const quote = init.quote ?? null;
  const fwdCandidates = quote?.mode === 'forward' ? (quote.attachments ?? []) : [];
  const [fwdIds, setFwdIds] = useState<Set<number>>(() => new Set(fwdCandidates.map((a) => a.attachmentId)));
  const [pickerOpen, setPickerOpen] = useState(false);
  /** 🗃 NAS 파일 고르기 (v2.313.0 · 개발요청 #27) */
  const [nasPickerOpen, setNasPickerOpen] = useState(false);
  const [sending, setSending] = useState(false);
  // 발송 성공 래치 — 닫히기 전 1.2초 동안 버튼이 되살아나 같은 메일이 두 번 나가는 것 방지
  const [sent, setSent] = useState(false);
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  /**
   * 지금 내려받는 중인 첨부 (v2.123.0).
   * ⭐한 번에 하나만 — 문서 첨부는 누를 때마다 서버가 **그 자리에서 다시 만든다**(견적서 엑셀 빼면
   *   크로뮴·LibreOffice 를 돌린다). 연달아 누르면 같은 문서를 여러 번 만들게 된다.
   */
  const [downloading, setDownloading] = useState<string | null>(null);
  /**
   * ⭐⭐⭐**빗장은 state 가 아니라 ref 로 건다.**
   *   `downloading` 은 화면에 「받는 중…」을 그리기 위한 것이고, **막는 데는 못 쓴다** —
   *   state 는 다음 그리기 때 반영되므로 **같은 순간에 세 번 누르면 셋 다 통과한다.**
   *   (실제로 그랬다 — 화면에서 세 번 눌러 보고 발견했다. 검사 그물은 못 잡는 자리다.)
   *   문서 첨부는 누를 때마다 서버가 그 자리에서 다시 만든다(크로뮴·LibreOffice 를 돌리는 종류도 있다).
   */
  const grabbing = useRef(false);

  /** 첨부 이름을 눌렀다 — 보내기 전에 파일로 받아 직접 열어 본다 */
  const grabDoc = async (d: MailDocRef, key: string): Promise<void> => {
    if (grabbing.current) return;
    grabbing.current = true;
    setDownloading(key);
    setFeedback(null);
    try {
      await downloadMailAttachment(d);
    } catch (e) {
      setFeedback({ kind: 'err', text: (e as Error).message });
    } finally {
      grabbing.current = false;
      setDownloading(null);
    }
  };
  const fileRef = useRef<HTMLInputElement>(null);

  const account = useQuery({ queryKey: ['mail', 'account'], queryFn: getMailAccount });

  /**
   * 「발송 시 생성」 첨부의 **실제 파일 이름** (v2.240.0 · 사용자 요청) — 「견적서 Q-2026-SM-0124 (PDF)」 같은
   *   이름표 대신 받는 사람이 보게 될 이름(「견적_261013_…pdf」). 서버가 생성 함수와 같은 규칙으로 정한다.
   *   ⭐키는 서버로 가는 좌표(docRefWire) — 첨부를 빼거나 더하면 다시 묻는다(겹침 「(2)」 도 따라 바뀐다).
   *   ⚠못 받으면(오류·느림) 원래 이름표를 그대로 둔다 — 발송을 막을 일이 아니다.
   */
  const docWire = docRefs.map(docRefWire);
  const docNames = useQuery({
    queryKey: ['mail', 'attachment-names', JSON.stringify(docWire)],
    queryFn: () => getMailAttachmentNames(docRefs),
    enabled: docRefs.length > 0,
    staleTime: 60_000,
    retry: 1,
  });

  /**
   * 캐시에 없던 경우(첫 열기 등)의 뒷북 — 도착하면 **한 번만** 붙인다(ref 빗장).
   * 조회가 다시 돌 때마다 붙이면 꼬리말이 여러 번 쌓인다.
   */
  const signature = useQuery({ queryKey: ['mail', 'signature'], queryFn: getMailSignature });
  const sigApplied = useRef(!!initialSigDoc);
  /**
   * ⚠사람이 이미 쓰기 시작했으면 **뒷북으로 붙이지 않는다.** 편집기는 바깥 값이 바뀌면
   *   내용을 통째로 갈아 끼우며 커서를 맨 앞으로 되돌린다 — 쓰던 사람에게는 글이 튀는 사고다.
   *   대신 아래 [+ 꼬리말 넣기] 단추로 직접 넣게 한다.
   */
  const bodyTouched = useRef(false);
  useEffect(() => {
    if (sigApplied.current || bodyTouched.current) return;
    const sigDoc = signatureDocOf(signature.data);
    if (!sigDoc) return;
    sigApplied.current = true;
    setBodyDoc((prev) => withSignature(prev, sigDoc));
  }, [signature.data]);

  /** 등록해 둔 꼬리말 (자동 넣기를 꺼 뒀어도 [+ 꼬리말 넣기] 로는 쓸 수 있다) */
  const sigAvailable = signatureDocOf(signature.data, true);
  /** ⭐본문을 실제로 들여다본 판정 — 지우면 안내가 함께 사라지고 단추가 다시 나타난다 */
  const sigInBody = !!sigAvailable && bodyHasSignature(bodyDoc, sigAvailable);

  const knownBytes =
    files.reduce((a, f) => a + f.size, 0) +
    docRefs.reduce((a, d) => a + (d.sizeHint ?? 0), 0) +
    fwdCandidates.filter((a) => fwdIds.has(a.attachmentId)).reduce((s, a) => s + Math.max(0, a.size), 0);
  const hasUnknownSize = docRefs.some((d) => d.sizeHint == null);
  // 첨부 개수 — 서버 상한(MAX_ATTACH_COUNT 20 · lib/mail-send/validate.ts)을 화면에서도 미리 센다 (v2.228.1 · 점검 실측:
  //   21개를 고르면 [보내기]가 켜진 채 서버의 영어 문구 「reach files limit」이 떴다)
  const attachCount = files.length + docRefs.length + fwdCandidates.filter((a) => fwdIds.has(a.attachmentId)).length;
  const overCount = attachCount > MAX_ATTACH_COUNT;
  const overBudget = knownBytes > MAX_TOTAL || overCount;

  const attachmentIds = new Set(
    docRefs.filter((d) => d.docType === 'attachment').map((d) => d.docId),
  );

  const notReady =
    !account.data?.configured || !account.data?.connected || account.data?.status === 'EXPIRED';

  const send = async (): Promise<void> => {
    if (!to.length) {
      setFeedback({ kind: 'err', text: '받는사람을 1명 이상 입력해 주세요.' });
      return;
    }
    if (!subject.trim()) {
      setFeedback({ kind: 'err', text: '제목을 입력해 주세요.' });
      return;
    }
    setSending(true);
    setFeedback(null);
    try {
      const r = await sendMail({
        to, cc, salesCc, subject: subject.trim(),
        body: docToPlainText(bodyDoc),
        bodyDoc: docForSave(bodyDoc),
        docRefs, files,
        ...(quote
          ? { quote: { mailId: quote.mailId, mode: quote.mode, attachmentIds: quote.mode === 'forward' ? [...fwdIds] : [] } }
          : {}),
      });
      setSent(true);
      void qc.invalidateQueries({ queryKey: ['mail', 'logs'] });
      // 연 쪽에 「나갔다」를 알린다 (v2.140.0 · 딜등록이 만료 안내 발송일을 적는다). 실패했으면 여기까지 안 온다.
      try { init.onSent?.({ to: r.to, cc: r.cc }); } catch (hookErr) { console.warn('onSent 처리 실패', hookErr); }
      setFeedback({
        kind: 'ok',
        // ⭐본문 그림은 「첨부」로 세지 않는다 — 꼬리말 로고 때문에 모든 메일이 「첨부 1개」가 된다
        text: `발송했습니다 — 받는사람 ${r.to.length}명`
          + `${r.attachmentCount ? ` · 첨부 ${r.attachmentCount}개` : ''}`
          + `${r.inlineCount ? ` · 본문 그림 ${r.inlineCount}장` : ''}`
          + `${r.attachmentCount || r.inlineCount ? ` (${fmtSize(r.totalBytes)})` : ''}`,
      });
      window.setTimeout(() => onClose(), 1200);
    } catch (e) {
      const err = e as Error & { mailCode?: string };
      const suffix =
        err.mailCode === 'not_connected' || err.mailCode === 'expired'
          ? ' — 마이페이지 > 메일 계정에서 연결해 주세요.'
          : '';
      setFeedback({ kind: 'err', text: `${err.message}${suffix}` });
    } finally {
      setSending(false);
    }
  };

  // v2.250.0: 조회 전용(viewer)도 메일을 쓴다(사용자 10-14) — 예전엔 여기서 「영업(sales) 이상 권한에서」 안내만 보였다.
  //   viewer 가 못 보는 문서(견적서·발주서 등)는 서버 첨부 단계(doc-source)가 막고, 그 문서의 ✉ 단추는 애초에 안 그린다.

  return (
    <>
      <div className="p-5 flex-1 overflow-y-auto space-y-3">
        {account.isSuccess && notReady && (
          <div className="px-3 py-2.5 bg-amber-50 border border-amber-200 text-amber-800 rounded text-sm">
            {!account.data.configured ? (
              <>네이버웍스 메일 앱이 아직 등록되지 않았습니다. 관리자에게 문의해 주세요.</>
            ) : account.data.status === 'EXPIRED' ? (
              <>메일 연결이 만료됐습니다. <a href="/mypage" className="underline font-medium">마이페이지</a>에서 다시 연결해 주세요.</>
            ) : (
              <>메일 계정이 아직 연결돼 있지 않습니다. <a href="/mypage" className="underline font-medium">마이페이지</a>에서 본인 웍스 계정을 연결하면 바로 보낼 수 있습니다.</>
            )}
          </div>
        )}

        <RecipientInput label="받는사람" chips={to} onChange={setTo} />
        {/* 영업 공용 메일은 서버가 발송 직전에 참조로 붙인다 (v2.176.0). 주소는 서버가 알려 준다.
            v2.250.0: **기본**으로 붙고 × 로 뺄 수 있다(사용자 10-14) — 뺐으면 salesCc=false 를 보내 서버도 안 붙인다.
            ⚠받는사람에 이미 있으면 서버가 참조에 안 붙이므로, 화면에서도 숨긴다 — 아니면 거짓말이 된다. */}
        {(() => {
          const sales = account.data?.alwaysCc ?? null;
          const inTo = !!sales && to.some((e) => e.trim().toLowerCase() === sales.toLowerCase());
          return (
            <>
              <RecipientInput
                label="참조"
                chips={cc}
                onChange={setCc}
                defaultAddr={sales && salesCc && !inTo ? sales : null}
                defaultHint="영업 공용 메일 — 기본으로 참조됩니다 (× 로 뺄 수 있습니다)"
                onRemoveDefault={() => setSalesCc(false)}
              />
              {/* 실수로 뺐을 때 되돌리는 길 — 사람이 참조칸에 직접 다시 적어도 된다 */}
              {sales && !salesCc && !inTo && !cc.some((e) => e.trim().toLowerCase() === sales.toLowerCase()) && (
                <div className="-mt-2 ml-[72px] text-xs text-slate-500">
                  영업 공용 메일({sales})을 참조에서 뺐습니다 ·{' '}
                  <button type="button" onClick={() => setSalesCc(true)} className="text-blue-600 hover:underline">
                    다시 넣기
                  </button>
                </div>
              )}
            </>
          );
        })()}

        <div className="flex items-center gap-2">
          <div className="w-16 text-xs font-medium text-slate-500 shrink-0">제목</div>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="ui-input flex-1"
            placeholder="제목"
          />
        </div>

        {/* 본문 — 블로그 편집기처럼 굵게·색·표·그림 (v2.124.0). 엑셀 표·화면 캡처를 Ctrl+V 로 붙여넣는다 */}
        <div className="flex items-start gap-2">
          <div className="w-16 pt-2 text-xs font-medium text-slate-500 shrink-0">본문</div>
          <div className="flex-1 min-w-0">
            <RichEditor
              label="본문"
              value={bodyDoc}
              onChange={(next) => {
                bodyTouched.current = true;
                setBodyDoc(next);
              }}
              limits={MAIL_DOC_LIMITS}
              minHeight={220}
              placeholder="본문 내용"
            />
            <div className="flex items-center gap-2 pt-1">
              <span className="text-xs text-slate-500">
                {sigInBody ? (
                  <>
                    본문 끝에 <strong className="font-medium text-slate-500">메일 꼬리말</strong>이 들어 있습니다 —
                    이 메일에서만 고치거나 지워도 되고, 늘 쓰는 인사말은 마이페이지 &gt; [메일 꼬리말] 에서 바꿉니다.
                  </>
                ) : (
                  <>메일 꼬리말(인사말 + 회사 정보)은 마이페이지 &gt; [메일 꼬리말] 에서 켜고 끕니다.</>
                )}
              </span>
              {/* ⭐지웠다가 되돌릴 때 · 자동 넣기를 꺼 뒀는데 이번엔 넣고 싶을 때 · 조회가 늦게 왔을 때.
                  ⭐**이미 들어 있으면 안 보인다** — 두 번 눌러 두 벌 붙는 것을 여기서 막는다. */}
              {sigAvailable && !sigInBody && (
                <button
                  type="button"
                  onClick={() => {
                    // 없는 부분만 넣는다 — 인사말만 지웠으면 인사말만, 표만 지웠으면 표만 (v2.211.0)
                    setBodyDoc((prev) => {
                      const miss = missingSignatureParts(prev, sigAvailable);
                      const parts = [
                        ...miss.personal,
                        ...(miss.personal.length && miss.company.length ? [{ type: 'paragraph' } as RichNode] : []),
                        ...miss.company,
                      ];
                      return withSignature(prev, parts.length ? { type: 'doc', content: parts } : sigAvailable);
                    });
                    // ⚠편집기는 값이 바뀌면 통째로 다시 그리며 **맨 위로 올라간다** — 방금 맨 아래에
                    //   붙은 꼬리말이 화면에 안 보인다. 그래서 말로 알려 준다.
                    setFeedback({ kind: 'ok', text: '본문 맨 아래에 메일 꼬리말을 넣었습니다.' });
                  }}
                  className="btn btn-secondary btn-sm ml-auto shrink-0"
                  title="저장해 둔 메일 꼬리말을 본문 끝에 넣습니다"
                >
                  + 꼬리말 넣기
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 답장·전달의 원문 (v2.205.0) — 원문은 서버가 다시 받아 본문 아래에 인용한다(여기서는 보여 주기만) */}
        {quote && (
          <div className="flex items-start gap-2">
            <div className="w-16 pt-1.5 text-xs font-medium text-slate-500 shrink-0">원문</div>
            <div className="flex-1 min-w-0 px-2.5 py-2 bg-slate-50 border border-slate-200 rounded text-xs text-slate-600 space-y-1.5">
              <div>
                {quote.mode === 'forward' ? '↪' : '↩'}{' '}
                <b className="text-slate-700">「{quote.originalSubject || '(제목 없음)'}」</b>
                <span className="text-slate-400"> · {quote.originalFrom}</span>
                <div className="text-xs text-slate-500 mt-0.5">
                  원문이 본문 아래에 인용되어 함께 나갑니다 (원문의 본문 그림은 「[그림]」으로 줄여 붙습니다)
                </div>
              </div>
              {fwdCandidates.length > 0 && (
                <div className="pt-1 border-t border-slate-200">
                  <div className="text-xs text-slate-500 mb-1">원문 첨부 — 함께 보낼 파일을 고르세요</div>
                  {fwdCandidates.map((a) => (
                    <label key={a.attachmentId} className="flex items-center gap-2 py-0.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={fwdIds.has(a.attachmentId)}
                        onChange={(e) => {
                          const next = new Set(fwdIds);
                          if (e.target.checked) next.add(a.attachmentId);
                          else next.delete(a.attachmentId);
                          setFwdIds(next);
                        }}
                      />
                      <span className="truncate text-slate-700">📎 {a.filename}</span>
                      <span className="ml-auto text-slate-400 whitespace-nowrap">{fmtSize(Math.max(0, a.size))}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* 첨부 */}
        <div className="flex items-start gap-2">
          <div className="w-16 pt-1.5 text-xs font-medium text-slate-500 shrink-0">첨부</div>
          <div className="flex-1">
            <div className="space-y-1">
              {docRefs.map((d, i) => {
                // 실제 파일 이름(v2.240.0) — 아직 못 받았거나 실패하면 원래 이름표
                const real = docNames.data?.[i]?.filename ?? null;
                const nameErr = docNames.data?.[i]?.error ?? null;
                const tag = d.label ?? `#${d.docId}`;
                return (
                <div
                  key={`d${i}`}
                  className="flex items-center gap-2 px-2 py-1 bg-slate-50 border border-slate-200 rounded text-sm"
                >
                  <span className="shrink-0">{d.docType === 'nas-file' ? '🗃' : '📄'}</span>
                  <span className="px-1 py-0.5 bg-indigo-50 text-indigo-700 rounded text-[11px] shrink-0">
                    {DOC_TYPE_LABEL[d.docType] ?? d.docType}
                  </span>
                  {/* ⭐이름을 누르면 내려받는다 — 보내기 전에 실제 파일을 열어 확인한다 (v2.123.0).
                      발송과 **같은 통로**를 타므로 지금 나갈 파일과 같은 코드에서 나온 것이다. */}
                  <button
                    type="button"
                    onClick={() => void grabDoc(d, `d${i}`)}
                    disabled={downloading !== null}
                    title={[
                      real ? `받는 사람에게 붙는 파일 이름: ${real}` : null,
                      real && real !== tag ? `(${tag})` : null,
                      nameErr ? `파일 이름을 미리 확인하지 못했습니다 — ${nameErr}` : null,
                      '눌러서 내려받기 — 보내기 전에 열어 확인할 수 있습니다 (보낼 때 서버가 다시 만듭니다)',
                    ].filter(Boolean).join('\n')}
                    className="flex-1 min-w-0 truncate text-left text-slate-700
                               hover:text-blue-700 hover:underline disabled:opacity-60 disabled:no-underline"
                  >
                    {downloading === `d${i}` ? '받는 중…' : (real ?? tag)}
                  </button>
                  <span className="ml-auto text-xs text-slate-500 whitespace-nowrap">
                    {d.sizeHint != null ? fmtSize(d.sizeHint) : '발송 시 생성'}
                  </span>
                  <button
                    type="button"
                    className="text-slate-400 hover:text-rose-600"
                    onClick={() => setDocRefs(docRefs.filter((_, j) => j !== i))}
                    title="첨부 제거"
                  >
                    ×
                  </button>
                </div>
                );
              })}
              {files.map((f, i) => (
                <div
                  key={`f${i}`}
                  className="flex items-center gap-2 px-2 py-1 bg-slate-50 border border-slate-200 rounded text-sm"
                >
                  <span className="shrink-0">💻</span>
                  {/* 내 PC 파일은 서버를 안 거친다 — 브라우저가 들고 있는 것을 그대로 저장 */}
                  <button
                    type="button"
                    onClick={() => downloadLocalFile(f)}
                    title="눌러서 내려받기 — 붙인 파일을 그대로 저장합니다"
                    className="flex-1 min-w-0 truncate text-left text-slate-700
                               hover:text-blue-700 hover:underline"
                  >
                    {f.name}
                  </button>
                  <span className="ml-auto text-xs text-slate-500 whitespace-nowrap">{fmtSize(f.size)}</span>
                  <button
                    type="button"
                    className="text-slate-400 hover:text-rose-600"
                    onClick={() => setFiles(files.filter((_, j) => j !== i))}
                    title="첨부 제거"
                  >
                    ×
                  </button>
                </div>
              ))}
              {docRefs.length === 0 && files.length === 0 ? (
                <div className="text-xs text-slate-500 py-1">첨부 없음</div>
              ) : (
                <div className="text-xs text-slate-500 pt-0.5">
                  첨부 이름을 누르면 내려받아 열어 볼 수 있습니다
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="btn btn-secondary btn-sm"
              >
                + 내 PC 파일
              </button>
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                className="btn btn-secondary btn-sm"
              >
                + 포털 보관 문서
              </button>
              <button
                type="button"
                onClick={() => setNasPickerOpen(true)}
                className="btn btn-secondary btn-sm"
                title="본인 NAS 계정으로 폴더를 열어 파일을 고릅니다 — 보낼 때 서버가 NAS 에서 가져와 붙입니다"
              >
                + NAS 파일
              </button>
              <span className={`ml-auto text-xs ${overBudget ? 'text-rose-600 font-medium' : 'text-slate-400'}`}>
                {overCount ? `첨부 ${attachCount}개 — 한 번에 ${MAX_ATTACH_COUNT}개까지 · ` : ''}
                {fmtSize(knownBytes)}{hasUnknownSize ? ' + 생성분' : ''} / 25MB
              </span>
            </div>
            <input
              ref={fileRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                const picked = Array.from(e.target.files ?? []);
                if (picked.length) setFiles([...files, ...picked]);
                e.target.value = '';
              }}
            />
          </div>
        </div>

        {feedback && (
          <div
            className={`px-3 py-2 rounded text-sm border ${
              feedback.kind === 'ok'
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                : 'bg-rose-50 border-rose-200 text-rose-800'
            }`}
          >
            {feedback.text}
          </div>
        )}
      </div>

      <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between">
        <div className="text-xs text-slate-500">
          {account.data?.nwEmail ? `보내는 사람: ${account.data.nwEmail}` : ''}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
          >
            취소
          </button>
          <button
            type="button"
            onClick={() => void send()}
            disabled={sending || sent || notReady || overBudget}
            className="btn btn-primary"
          >
            {sending ? '보내는 중…' : sent ? '발송 완료' : '✉ 보내기'}
          </button>
        </div>
      </div>

      <NasFilePicker
        open={nasPickerOpen}
        existingKeys={new Set(
          docRefs
            .filter((d) => d.docType === 'nas-file')
            .map((d) => { const p = d.params as { path: string[]; name: string }; return nasFileKey(p.path, p.name); }),
        )}
        onClose={() => setNasPickerOpen(false)}
        onAdd={(refs) => setDocRefs([...docRefs, ...refs])}
      />
      <PortalDocPicker
        open={pickerOpen}
        initialSalesId={init.salesId ?? null}
        existingIds={attachmentIds}
        onClose={() => setPickerOpen(false)}
        onAdd={(items) =>
          setDocRefs([
            ...docRefs,
            ...items.map((a): MailDocRef => ({
              docType: 'attachment',
              docId: a.id,
              label: a.filename,
              sizeHint: a.file_size,
            })),
          ])
        }
      />
    </>
  );
}

export function MailComposeModal() {
  const state = useSyncExternalStore(subscribeMailCompose, getMailComposeState);
  // 메일 쓰기 — 입력 창 (v2.231.0 · H-4) · 열려 있을 때만 쌓인다
  useDialogLayer(state.open, null);
  const { user } = useAuth();
  /**
   * 메일 꼬리말을 **창이 열리기 전에** 미리 받아 둔다 (v2.125.0).
   * 이 껍데기는 App 에 늘 마운트돼 있다 — 여기서 받아 두면 ✉ 를 누르는 순간 본문이
   * 처음부터 꼬리말이 붙은 채로 뜬다(늦게 도착해 툭 튀어나오지 않는다).
   * (v2.250.0 부터 viewer 도 /mail/* 을 쓴다 — 전엔 403 이라 viewer 는 부르지 않았다.)
   */
  useQuery({
    queryKey: ['mail', 'signature'],
    queryFn: getMailSignature,
    enabled: !!user,
    staleTime: 5 * 60_000,
  });
  if (!state.open) return null;

  return (
    // z-[90] — 호스트 모달(z-70)·그 서브모달(z-85)·판보 근거문서(z-80) 어디서 열려도 위에 뜨게.
    //   (미리보기 z-80 은 ✉ 클릭 시 먼저 닫히지만, 다른 호스트는 열린 채 이 모달이 겹친다)
    <div className="fixed inset-0 z-[90] bg-black/40 flex items-center justify-center p-4">
      {/* max-w-3xl — 본문이 서식 편집기가 되면서(v2.124.0) 도구 막대·표가 들어갈 폭이 필요하다 */}
      <div className="bg-white rounded shadow-xl w-full max-w-3xl max-h-[92vh] flex flex-col">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="ui-modal-title">
            {state.init.quote?.mode === 'reply' ? '↩ 답장'
              : state.init.quote?.mode === 'replyAll' ? '↩ 전체 답장'
                : state.init.quote?.mode === 'forward' ? '↪ 전달'
                  : '✉ 메일 보내기'}
          </h2>
          <button
            type="button"
            onClick={closeMailCompose}
            className="ui-close"
          >
            ×
          </button>
        </div>
        {/* seq 키 — 열 때마다 입력 상태 초기화 */}
        <ComposeBody key={state.seq} onClose={closeMailCompose} />
      </div>
    </div>
  );
}
